import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { SubscriptionPlan } from '../types';
import { teamApi, OwnBilling } from '../api/team';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { Toast, useToast } from '../components/Toast';

const planLabel: Record<SubscriptionPlan, string> = {
  FREE: 'Testowy',
  STANDARD: 'Standard',
  PRO: 'Pro',
};

/** Opisy planów na kafelkach — zgodne z realnym gatingiem (PlanGuard: AI = PRO albo Testowy). */
const PLAN_TILES: { plan: SubscriptionPlan; tagline: string; accent: string; features: string[]; footnote?: string }[] = [
  {
    plan: 'FREE',
    tagline: 'Do pilotażu i testów platformy',
    accent: '#949ba4',
    features: [
      'Wszystkie funkcje bez limitów (w tym AI)',
      'Bez faktur i opłat',
      'Idealny na start i okres próbny',
    ],
    footnote: 'Przydzielany ręcznie przez administratora systemu.',
  },
  {
    plan: 'STANDARD',
    tagline: 'Codzienna obsługa serwisowa',
    accent: '#1e78d6',
    features: [
      'Katalog maszyn z kodami QR',
      'Zgłoszenia serwisowe klientów (tickety)',
      'Notatki serwisowe i dokumentacja plikowa',
      'Zespół pracowników i zaproszenia',
      'Portal klienta',
    ],
  },
  {
    plan: 'PRO',
    tagline: 'Standard + sztuczna inteligencja',
    accent: '#2e9956',
    features: [
      'Wszystko z planu Standard',
      'Automatyczne odpowiedzi AI na zgłoszenia (na bazie dokumentacji)',
      'Analiza AI zgłoszeń dla serwisanta',
      'Indeksowanie dokumentacji PDF',
      'Wyszukiwarka semantyczna notatek',
    ],
  },
];
const invoiceStatusLabel: Record<string, string> = {
  PENDING: 'Oczekuje',
  ISSUED: 'Wystawiona',
  FAILED: 'Błąd',
};

/** Samoobsługa abonamentu admina integratora: podgląd planu i faktur, prośba o zmianę planu. */
const Subscription: React.FC = () => {
  const { user } = useAuth();
  const [billing, setBilling] = useState<OwnBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** Plan, o który user prosi — otwiera modal z wiadomością. */
  const [requestedPlan, setRequestedPlan] = useState<SubscriptionPlan | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  useEffect(() => {
    teamApi.getBilling()
      .then(({ data }) => setBilling(data))
      .catch((e: any) => setError(e?.response?.data?.message || 'Nie udało się pobrać danych abonamentu'))
      .finally(() => setLoading(false));
  }, []);

  if (!user?.roles?.includes('EMPLOYEE_ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const requestChange = async () => {
    if (!requestedPlan) return;
    const plan = requestedPlan;
    setSending(true);
    try {
      const { data } = await teamApi.requestPlanChange(plan, message.trim() || undefined);
      setRequestedPlan(null);
      setMessage('');
      showToast('success', data.message);
      // Odśwież widok, żeby od razu pokazać „już zawnioskowano o plan …".
      teamApi.getBilling().then(({ data: b }) => setBilling(b)).catch(() => { /* zostaw poprzedni stan */ });
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się wysłać prośby.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Abonament</h1>

      {error && <div className="auth-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : !billing ? (
        <div className="empty-state">Brak danych abonamentu</div>
      ) : (
        <>
          <div style={{ maxWidth: 900, margin: '0 auto 1.5rem' }}>
          <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Dostępne plany</h2>
          {billing.pendingPlanRequest && (
            <div className="card" style={{
              padding: '0.75rem 1rem', marginBottom: '0.75rem',
              borderLeft: '4px solid var(--color-primary)',
              display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
            }}>
              <span className="badge badge-warning">Prośba w toku</span>
              <span className="item-meta" style={{ margin: 0 }}>
                Zawnioskowano już o plan <strong>{planLabel[billing.pendingPlanRequest]}</strong>
                {billing.pendingPlanRequestedAt
                  && ` (${new Date(billing.pendingPlanRequestedAt).toLocaleDateString('pl-PL')})`}
                {' '}— administrator systemu skontaktuje się z Tobą. Możesz wysłać kolejną prośbę, jeśli chcesz ją zmienić.
              </span>
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1rem',
          }}>
            {PLAN_TILES.map((tile) => {
              const isCurrent = billing.plan === tile.plan;
              return (
                <div key={tile.plan} className="card" style={{
                  padding: '1.25rem',
                  borderTop: `4px solid ${tile.accent}`,
                  outline: isCurrent ? '2px solid var(--color-primary)' : undefined,
                  display: 'flex', flexDirection: 'column', gap: '0.75rem',
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1.125rem', fontWeight: 700 }}>{planLabel[tile.plan]}</span>
                      {isCurrent && <span className="badge badge-success">Obecny plan</span>}
                    </div>
                    <div className="item-meta" style={{ marginTop: '0.25rem' }}>{tile.tagline}</div>
                    {isCurrent && billing.effectivePriceNet != null && (
                      <div className="item-meta" style={{ marginTop: '0.25rem' }}>
                        {billing.effectivePriceNet.toFixed(2)} zł netto / mies.
                      </div>
                    )}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'grid', gap: '0.35rem', fontSize: '0.875rem' }}>
                    {tile.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  {tile.footnote && (
                    <div className="item-meta" style={{ marginTop: 'auto' }}>{tile.footnote}</div>
                  )}
                  {!isCurrent && tile.plan !== 'FREE' && (
                    <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 'auto' }}
                      onClick={() => setRequestedPlan(tile.plan)}>
                      {billing.pendingPlanRequest === tile.plan ? 'Ponów prośbę' : 'Poproś o ten plan'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>

          {requestedPlan && (
            <Modal title={`Prośba o plan ${planLabel[requestedPlan]}`} dismissable={!sending}
              onClose={() => { setRequestedPlan(null); setMessage(''); }}>
              <p className="item-meta" style={{ marginBottom: '0.75rem' }}>
                Prośba trafi mailem do administratora systemu, który zmienia plany —
                skontaktuje się z Tobą w sprawie szczegółów.
              </p>
              <div className="form-group">
                <label className="form-label">Wiadomość (opcjonalnie)</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  placeholder="np. od kiedy, ilu pracowników…"
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" disabled={sending}
                  onClick={() => { setRequestedPlan(null); setMessage(''); }}>
                  Anuluj
                </button>
                <button type="button" className="btn btn-primary" disabled={sending} onClick={requestChange}>
                  {sending ? 'Wysyłanie…' : 'Wyślij'}
                </button>
              </div>
            </Modal>
          )}

          <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Faktury</h2>
          {billing.invoices.length === 0 ? (
            <div className="empty-state">Brak faktur</div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden', maxWidth: 640 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-surface-alt)' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem' }}>Okres</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem' }}>Plan</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem 1rem' }}>Netto</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem' }}>Nr faktury</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.invoices.map((inv) => (
                    <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td className="mono" style={{ padding: '0.5rem 1rem' }}>{inv.period}</td>
                      <td style={{ padding: '0.5rem 1rem' }}>{planLabel[inv.plan]}</td>
                      <td className="mono" style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                        {inv.priceNet.toFixed(2)} zł
                      </td>
                      <td className="mono" style={{ padding: '0.5rem 1rem' }}>{inv.invoiceNumber || '—'}</td>
                      <td style={{ padding: '0.5rem 1rem' }}>{invoiceStatusLabel[inv.status] || inv.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default Subscription;
